import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { BarChart3, Brain, Zap, User, Mail, Lock, AlertCircle, ArrowRight, GraduationCap } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import BRAND from '../brand';
import { trackRegistration } from '../utils/analytics';
import axios from 'axios';

// Inline SVG icons for OAuth providers
const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const AppleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
  </svg>
);

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register, loginWithGoogle, loginWithApple } = useAuth();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    gdprConsent: false,
    country: '',
    hp_confirm_token: '',  // Honeypot field
  });
  const [error, setError] = useState('');
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [verificationEmailSent, setVerificationEmailSent] = useState(true);
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState('');
  const [isStudentEmail, setIsStudentEmail] = useState(false);
  const [providers, setProviders] = useState({ google: true, apple: true }); // Default visible, API confirms
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Fetch which OAuth providers are enabled
  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/auth/providers`);
        if (res.ok) setProviders(await res.json());
      } catch (e) { console.warn('Provider check failed:', e); }
    };
    fetchProviders();
  }, [BACKEND_URL]);

  const handleEmailChange = (email) => {
    setFormData(prev => ({ ...prev, email }));
    setIsStudentEmail(email.toLowerCase().trim().endsWith('.edu'));
  };

  const validateForm = () => {
    if (!formData.fullName.trim() || formData.fullName.trim().length < 2) {
      return 'Full name must be at least 2 characters';
    }
    if (!formData.email.trim()) {
      return 'Email is required';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      return 'Please enter a valid email address';
    }
    if (formData.password.length < 8) {
      return 'Password must be at least 8 characters';
    }
    if (!/[a-zA-Z]/.test(formData.password) && !/\p{L}/u.test(formData.password)) {
      return 'Password must contain at least one letter';
    }
    if (!/[0-9]/.test(formData.password)) {
      return 'Password must contain at least one number';
    }
    if (formData.password !== formData.confirmPassword) {
      return 'Passwords do not match';
    }
    if (!formData.gdprConsent) {
      return 'You must consent to data processing to create an account';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    const result = await register(
      formData.email.trim().toLowerCase(),
      formData.password,
      formData.fullName.trim(),
      formData.gdprConsent,
      formData.country || null,
      formData.hp_confirm_token || ''
    );

    if (!result.success) {
      setError(result.error);
      setLoading(false);
    } else {
      trackRegistration('email');
      setRegisteredEmail(formData.email.trim().toLowerCase());
      setVerificationEmailSent(result.verification_email_sent !== false);
      setRegistrationComplete(true);
      setLoading(false);
    }
  };

  // --- Google Sign-In ---
  const handleGoogleSignIn = useCallback(async () => {
    setError('');
    setOauthLoading('google');
    try {
      if (!window.google?.accounts) {
        await new Promise((resolve, reject) => {
          const s = document.createElement('script');
          s.src = 'https://accounts.google.com/gsi/client';
          s.async = true;
          s.onload = resolve;
          s.onerror = () => reject(new Error('Failed to load Google Sign-In'));
          document.head.appendChild(s);
        });
      }
      const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
      if (!googleClientId) {
        setError('Google Sign-In is not configured');
        setOauthLoading('');
        return;
      }
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: async (response) => {
          const result = await loginWithGoogle(response.credential);
          if (!result.success) {
            setError(result.error);
          } else {
            trackRegistration('google');
            navigate('/');
          }
          setOauthLoading('');
        },
      });
      window.google.accounts.id.prompt((notification) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          const btn = document.createElement('div');
          btn.id = 'g_id_onload_tmp';
          btn.style.display = 'none';
          document.body.appendChild(btn);
          window.google.accounts.id.renderButton(btn, { type: 'icon', size: 'large' });
          btn.querySelector('div[role="button"]')?.click();
          setTimeout(() => btn.remove(), 100);
          setOauthLoading('');
        }
      });
    } catch (err) {
      setError('Google Sign-In unavailable. Please try again.');
      setOauthLoading('');
    }
  }, [loginWithGoogle, navigate]);

  // --- Apple Sign-In ---
  const handleAppleSignIn = useCallback(async () => {
    setError('');
    setOauthLoading('apple');
    try {
      if (!window.AppleID) {
        await new Promise((resolve, reject) => {
          const s = document.createElement('script');
          s.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
          s.async = true;
          s.onload = resolve;
          s.onerror = () => reject(new Error('Failed to load Apple Sign-In'));
          document.head.appendChild(s);
        });
      }
      const appleClientId = process.env.REACT_APP_APPLE_CLIENT_ID;
      const redirectUri = window.location.origin;
      if (!appleClientId) {
        setError('Apple Sign-In is not configured');
        setOauthLoading('');
        return;
      }
      window.AppleID.auth.init({
        clientId: appleClientId,
        scope: 'name email',
        redirectURI: redirectUri,
        usePopup: true,
      });
      const response = await window.AppleID.auth.signIn();
      const idToken = response.authorization?.id_token;
      const fullName = response.user
        ? `${response.user.name?.firstName || ''} ${response.user.name?.lastName || ''}`.trim()
        : '';
      if (idToken) {
        const result = await loginWithApple(idToken, fullName);
        if (!result.success) {
          setError(result.error);
        } else {
          navigate('/');
        }
      } else {
        setError('Apple Sign-In failed');
      }
    } catch (err) {
      if (err?.error !== 'popup_closed_by_user') {
        setError('Apple Sign-In unavailable. Please try again.');
      }
    }
    setOauthLoading('');
  }, [loginWithApple, navigate]);

  const hasOAuth = providers.google || providers.apple;

  if (registrationComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-orange-50 p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Account Created!</h2>
            <p className="text-gray-600 mb-4">
              We've sent a verification email to <strong>{registeredEmail}</strong>.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4 text-left">
              <p className="text-sm text-amber-800 font-semibold mb-1">⚠️ Check your Spam / Junk folder</p>
              <p className="text-sm text-amber-700">
                The verification email often lands in your Spam or Junk folder. Please check there if you don't see it in your inbox. The link expires in 7 days.
              </p>
            </div>
            {!verificationEmailSent && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-red-700">The verification email could not be sent. Please use the resend button below.</p>
              </div>
            )}
            <div className="flex flex-col space-y-3">
              <button
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('era_auth_token');
                    await axios.post(`${BACKEND_URL}/api/auth/resend-verification`, {}, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    alert('Verification email resent. Please check your inbox and spam folder.');
                  } catch (e) {
                    alert(e.response?.data?.detail || 'Failed to resend. Please try again.');
                  }
                }}
                className="text-sm text-orange-600 hover:text-orange-700 font-semibold"
              >
                Resend verification email
              </button>
              <button
                onClick={() => navigate('/')}
                className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-xl font-semibold transition-colors"
              >
                Continue to Platform
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col md:flex-row min-h-screen">
      {/* Left Panel — Brand */}
      <div className="hidden md:flex md:w-1/2 bg-gray-900 relative overflow-hidden flex-col justify-between p-12">
        {/* Background gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-orange-600/20 via-transparent to-purple-600/10" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-orange-500/5 rounded-full blur-3xl" />

        <div className="relative z-10">
          {/* Logo & Brand */}
          <div className="flex items-center space-x-4 mb-16">
            <img
              src={BRAND.logo}
              alt="Era Digital"
              className="w-14 h-14 rounded-full object-cover"
              style={{ boxShadow: `0 4px 20px ${BRAND.colors.orangeGlow}` }}
            />
            <div>
              <h1 className="text-2xl font-bold text-white">
                Era
              </h1>
              <p className="text-gray-400 text-sm">{BRAND.tagline}</p>
            </div>
          </div>

          {/* Headline */}
          <h2 className="text-4xl font-bold text-white leading-tight mb-4">
            Your Command Center for
            <br />
            <span className="bg-gradient-to-r from-orange-400 to-orange-500 bg-clip-text text-transparent">
              Market Intelligence
            </span>
          </h2>
          <p className="text-gray-400 text-lg mb-12 max-w-md">
            Real-time data, AI-powered insights — all in one platform.
          </p>

          {/* Feature Cards */}
          <div className="space-y-4">
            <div className="flex items-center space-x-4 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
              <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <BarChart3 className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <p className="text-white font-medium text-sm">Real-Time Market Data</p>
                <p className="text-gray-400 text-xs">Crypto, equities, commodities & macro</p>
              </div>
            </div>

            <div className="flex items-center space-x-4 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
              <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Brain className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-white font-medium text-sm">AI-Powered Analysis</p>
                <p className="text-gray-400 text-xs">Daily briefings & technical insights</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="relative z-10 text-gray-500 text-xs">
          &copy; 2026 Era Digital Limited. All rights reserved.
        </p>
      </div>

      {/* Right Panel — Form */}
      <div className="flex-1 flex items-center justify-center bg-white p-6 md:p-12 overflow-y-auto">
        <div className="w-full max-w-md py-8 md:py-0">
          {/* Mobile Logo */}
          <div className="flex md:hidden items-center justify-center space-x-3 mb-10">
            <img
              src={BRAND.logo}
              alt="Era Digital"
              className="w-12 h-12 rounded-full object-cover"
              style={{ boxShadow: `0 4px 16px ${BRAND.colors.orangeGlow}` }}
            />
            <h1 className="text-2xl font-bold text-gray-900">
              Era
            </h1>
          </div>

          {/* Heading */}
          <div className="mb-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Create your account</h2>
            <p className="text-gray-500">Get started with Era for free</p>
          </div>

          {/* OAuth Buttons */}
          {hasOAuth && (
            <div className="space-y-3 mb-5">
              {providers.google && (
                <button
                  onClick={handleGoogleSignIn}
                  disabled={!!oauthLoading}
                  className="w-full h-12 flex items-center justify-center space-x-3 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 active:scale-[0.99] transition-all duration-150 shadow-sm disabled:opacity-60"
                >
                  {oauthLoading === 'google' ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-400 border-t-transparent" />
                  ) : (
                    <>
                      <GoogleIcon />
                      <span>Continue with Google</span>
                    </>
                  )}
                </button>
              )}
              {providers.apple && (
                <button
                  onClick={handleAppleSignIn}
                  disabled={!!oauthLoading}
                  className="w-full h-12 flex items-center justify-center space-x-3 bg-black rounded-xl text-sm font-medium text-white hover:bg-gray-900 active:scale-[0.99] transition-all duration-150 shadow-sm disabled:opacity-60"
                >
                  {oauthLoading === 'apple' ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                  ) : (
                    <>
                      <AppleIcon />
                      <span>Continue with Apple</span>
                    </>
                  )}
                </button>
              )}

              {/* Divider */}
              <div className="relative flex items-center py-1">
                <div className="flex-grow border-t border-gray-200" />
                <span className="flex-shrink-0 mx-4 text-xs text-gray-400 font-medium uppercase tracking-wider">or</span>
                <div className="flex-grow border-t border-gray-200" />
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Full Name
              </label>
              <div className="relative">
                <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={formData.fullName}
                  onChange={(e) => setFormData(prev => ({ ...prev, fullName: e.target.value }))}
                  placeholder="Enter your full name"
                  className="w-full h-12 pl-11 pr-4 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-colors"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full h-12 pl-11 pr-4 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-colors"
                  required
                />
              </div>
              {isStudentEmail && (
                <div className="mt-2 flex items-center space-x-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                  <GraduationCap className="w-4 h-4 text-green-600 flex-shrink-0" />
                  <span className="text-xs font-medium text-green-700">Student email detected — you qualify for Student pricing!</span>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="Create a strong password"
                  className="w-full h-12 pl-11 pr-4 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-colors"
                  required
                />
              </div>
              <p className="text-xs text-gray-400 mt-1.5 ml-0.5">
                Min 8 characters with at least one letter and one number
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Confirm Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  placeholder="Confirm your password"
                  className="w-full h-12 pl-11 pr-4 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-colors"
                  required
                />
              </div>
            </div>

            {/* Country Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Country</label>
              <select
                value={formData.country}
                onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
                className="w-full h-12 px-4 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-colors"
              >
                <option value="">Select your country (optional)</option>
                <option value="US">United States</option>
                <option value="GB">United Kingdom</option>
                <option value="DE">Germany</option>
                <option value="FR">France</option>
                <option value="CA">Canada</option>
                <option value="AU">Australia</option>
                <option value="JP">Japan</option>
                <option value="SG">Singapore</option>
                <option value="AE">United Arab Emirates</option>
                <option value="CH">Switzerland</option>
                <option value="NL">Netherlands</option>
                <option value="IE">Ireland</option>
                <option value="SE">Sweden</option>
                <option value="NO">Norway</option>
                <option value="DK">Denmark</option>
                <option value="NZ">New Zealand</option>
                <option value="HK">Hong Kong</option>
                <option value="IN">India</option>
                <option value="BR">Brazil</option>
                <option value="MX">Mexico</option>
                <option value="ZA">South Africa</option>
                <option value="NG">Nigeria</option>
                <option value="KE">Kenya</option>
                <option value="TR">Turkey</option>
                <option value="PL">Poland</option>
                <option value="IT">Italy</option>
                <option value="ES">Spain</option>
                <option value="PT">Portugal</option>
                <option value="KR">South Korea</option>
                <option value="TW">Taiwan</option>
                <option value="TH">Thailand</option>
                <option value="MY">Malaysia</option>
                <option value="PH">Philippines</option>
                <option value="ID">Indonesia</option>
                <option value="OTHER">Other</option>
              </select>
            </div>

            {/* Honeypot field — hidden from humans, bots fill it */}
            <div style={{ position: 'absolute', left: '-9999px', top: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden="true" tabIndex="-1">
              <input
                type="text"
                name="hp_confirm_token"
                value={formData.hp_confirm_token}
                onChange={(e) => setFormData(prev => ({ ...prev, hp_confirm_token: e.target.value }))}
                autoComplete="new-password"
                data-lpignore="true"
                data-1p-ignore="true"
                data-bwignore="true"
                data-form-type="other"
                tabIndex="-1"
              />
            </div>

            {/* GDPR Consent */}
            <div className="flex items-start space-x-3 pt-1">
              <Checkbox
                id="gdpr-consent"
                checked={formData.gdprConsent}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, gdprConsent: checked }))}
                className="mt-0.5"
              />
              <Label htmlFor="gdpr-consent" className="text-xs text-gray-500 leading-relaxed cursor-pointer">
                I consent to Era Digital Limited processing my personal data in accordance with the{' '}
                <Link to="/privacy-policy" className="text-orange-600 hover:text-orange-700 underline" target="_blank">
                  Privacy Policy
                </Link>
                . I can withdraw my consent at any time.
              </Label>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3.5 flex items-start space-x-2.5">
                <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white h-12 text-base font-semibold rounded-xl shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 transition-all duration-200"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                  Creating account...
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  Create Account
                  <ArrowRight className="w-5 h-5 ml-2" />
                </div>
              )}
            </Button>
          </form>

          {/* Divider */}
          <div className="mt-8 pt-8 border-t border-gray-100 text-center">
            <p className="text-sm text-gray-500">
              Already have an account?{' '}
              <Link to="/login" className="text-orange-600 hover:text-orange-700 font-semibold transition-colors">
                Sign In
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
